// The NeuralRepo mark (D-83): the D-77 forward pass in miniature on a rounded-square
// tile — a 7×7 cell grid, 2→3→2 neurons, the firing route in Ember, the other
// neurons and edges in deeper (dark) / paler (light) oranges. At 32 px and below,
// the simplified drawing: seven neurons and the lit route, no grid.
// Ported from the P3-20 round 2 canvas generator (Mark, kind "cells"). No DOM.

export type MarkGround = 'charcoal' | 'paper' | 'iosdark' | 'tinted';

interface Palette {
  tile: string;
  line: string;
  slate: string;
  lit: string;
  neuron: string;
  edge: string;
}

const PALETTES: Record<MarkGround, Palette> = {
  charcoal: { tile: '#19191A', line: 'none', slate: '#606786', lit: '#E8723A', neuron: '#A9532B', edge: '#7A3D22' },
  paper: { tile: '#FAFAF8', line: '#E5E3DC', slate: '#7D84A5', lit: '#B4501F', neuron: '#D9895F', edge: '#E9B496' },
  iosdark: { tile: '#0C0C0D', line: 'none', slate: '#4E5470', lit: '#EC7C46', neuron: '#9A4C28', edge: '#6A3520' },
  tinted: { tile: '#1C1C1E', line: 'none', slate: '#48484A', lit: '#F2F2F2', neuron: '#8E8E93', edge: '#5A5A5E' },
};

export interface MarkOptions {
  /** Rendered size in px (the drawing is on a 64 grid). */
  size: number;
  ground?: MarkGround;
  /** 'auto' draws the simplified mark at ≤ 32 px. */
  detail?: 'auto' | 'full' | 'simple';
  /** false: no tile (the mark on whatever is behind it). */
  tile?: boolean;
  /**
   * Scale the drawing inside a full-bleed square tile (0–1), for maskable PWA icons
   * whose safe zone is the central 80 %. Implies a square, unrounded tile.
   */
  inset?: number;
}

const f = (n: number) => Math.round(n * 100) / 100;

function dot(x: number, y: number, r: number): string {
  return `M${f(x - r)} ${f(y)}a${f(r)} ${f(r)} 0 1 0 ${f(2 * r)} 0a${f(r)} ${f(r)} 0 1 0 ${f(-2 * r)} 0`;
}

const L: Array<Array<[number, number]>> = [
  [[0, 2], [0, 4]],
  [[3, 1], [3, 3], [3, 5]],
  [[6, 2], [6, 4]],
];
const ACT = [0, 1, 1];

function drawing(simple: boolean, size: number) {
  const d = { grid: '', edge: '', litCells: '', neuron: '', litNeuron: '', litRoute: '' };
  let gridOpacity = 0.5;
  let routeWidth = 2.6;

  if (simple) {
    const q = (i: number) => 9 + 7.67 * i;
    const A = L.map((ns, li) => ns[ACT[li]!]!);
    d.litRoute = `M${f(q(A[0]![0]))} ${f(q(A[0]![1]))}L${f(q(A[1]![0]))} ${f(q(A[1]![1]))}L${f(q(A[2]![0]))} ${f(q(A[2]![1]))}`;
    routeWidth = size <= 16 ? 5 : 4;
    L.forEach((ns, li) =>
      ns.forEach((n, i) => {
        if (i === ACT[li]) d.litNeuron += dot(q(n[0]), q(n[1]), size <= 16 ? 6.4 : 5.6);
        else d.neuron += dot(q(n[0]), q(n[1]), size <= 16 ? 5 : 4.6);
      }),
    );
    return { d, gridOpacity, routeWidth };
  }

  const p = (i: number) => 11 + 7 * i;
  type Role = ['n', boolean] | ['h'] | ['y'];
  const role = new Map<string, Role>();
  const RANK = { n: 3, h: 2, y: 1 } as const;
  const set = (x: number, y: number, r: Role) => {
    const key = `${x},${y}`;
    const cur = role.get(key);
    if (!cur || RANK[cur[0]] < RANK[r[0]]) role.set(key, r);
  };
  for (let li = 0; li < 2; li++) {
    L[li]!.forEach((a, i) =>
      L[li + 1]!.forEach((b, j) => {
        const hot = ACT[li] === i && ACT[li + 1] === j;
        const n = Math.max(Math.abs(b[0] - a[0]), Math.abs(b[1] - a[1]));
        for (let t = 1; t < n; t++) {
          set(Math.round(a[0] + ((b[0] - a[0]) * t) / n), Math.round(a[1] + ((b[1] - a[1]) * t) / n), [hot ? 'h' : 'y']);
        }
      }),
    );
  }
  L.forEach((ns, li) => ns.forEach((n, i) => set(n[0], n[1], ['n', i === ACT[li]])));
  for (let y = 0; y < 7; y++) {
    for (let x = 0; x < 7; x++) {
      const r = role.get(`${x},${y}`);
      if (!r) d.grid += dot(p(x), p(y), 1.05);
      else if (r[0] === 'n') {
        if (r[1]) d.litNeuron += dot(p(x), p(y), 3.4);
        else d.neuron += dot(p(x), p(y), 3.1);
      } else if (r[0] === 'h') d.litCells += dot(p(x), p(y), 1.8);
      else d.edge += dot(p(x), p(y), 1.6);
    }
  }
  gridOpacity = size >= 120 ? 0.5 : 0.38;
  return { d, gridOpacity, routeWidth };
}

/** The mark as a standalone <svg> element string. */
export function markSvg(opts: MarkOptions): string {
  const S = opts.size;
  const c = PALETTES[opts.ground ?? 'charcoal'];
  const detail = opts.detail ?? 'auto';
  const simple = detail === 'simple' || (detail === 'auto' && S <= 32);
  const { d, gridOpacity, routeWidth } = drawing(simple, S);

  let tile = '';
  if (opts.inset !== undefined) tile = `<rect width="64" height="64" fill="${c.tile}"/>`;
  else if (opts.tile !== false) {
    tile =
      c.line !== 'none'
        ? `<rect x="0.5" y="0.5" width="63" height="63" rx="13.6" fill="${c.tile}" stroke="${c.line}" stroke-width="1"/>`
        : `<rect width="64" height="64" rx="14" fill="${c.tile}"/>`;
  }

  const paths = [
    d.grid && `<path d="${d.grid}" fill="${c.slate}" fill-opacity="${gridOpacity}"/>`,
    d.litRoute &&
      `<path d="${d.litRoute}" fill="none" stroke="${c.lit}" stroke-width="${routeWidth}" stroke-linecap="round" stroke-linejoin="round"/>`,
    d.edge && `<path d="${d.edge}" fill="${c.edge}"/>`,
    d.litCells && `<path d="${d.litCells}" fill="${c.lit}"/>`,
    d.neuron && `<path d="${d.neuron}" fill="${c.neuron}"/>`,
    d.litNeuron && `<path d="${d.litNeuron}" fill="${c.lit}"/>`,
  ]
    .filter(Boolean)
    .join('');

  const inset = opts.inset;
  const body =
    inset !== undefined ? `<g transform="translate(${f(32 - 32 * inset)} ${f(32 - 32 * inset)}) scale(${inset})">${paths}</g>` : paths;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${S}" height="${S}" viewBox="0 0 64 64" role="img" aria-label="NeuralRepo">${tile}${body}</svg>`;
}
